import { useAtom } from '@reatom/npm-react'
import React from 'react'
import styled from 'styled-components'

import { Numeric, Typography } from '@blade/ui-kit'
import { Button } from '@blade/ui-kit'
import { userFullNameOrUsernameAtom } from '@root/entities/auth'
import { UserAvatar } from '@root/entities/auth/ui'
import { SummaryChart } from '@root/entities/summary/ui/summary-chart/common/summary-chart'
import { USER_TRADING_ANALYTICS_PERIOD_LABEL_MAP } from '@root/features/user/user-trading-analytics/models/user-trading-analytics-period'
import { useTranslation } from '@root/shared/localization'
import { Gear2Icon } from '@root/shared/ui-kit'
import {
  notificationProfileWidgetChartDataAtom,
  notificationProfileWidgetAtom,
} from '@root/widgets/notification-widgets/atoms'

import { getPnlTrend, type NotificationWidgetVariant } from '../../model'

type Props = {
  variant: NotificationWidgetVariant
}

export const NotificationWidgetProfile: React.FC<Props> = ({ variant }) => {
  const { t } = useTranslation()
  const [{ period, pnlUsd, pnlPercents }] = useAtom(
    notificationProfileWidgetAtom,
  )
  const [chartData = []] = useAtom(notificationProfileWidgetChartDataAtom)
  const [username] = useAtom(userFullNameOrUsernameAtom)

  const trend = getPnlTrend(pnlUsd ?? pnlPercents)

  return (
    <Root data-variant={variant}>
      <Heading>
        <Label>{t('notification-center.my_profile')}</Label>
        <SettingsButton
          variant="ghost"
          size="h28"
          iconLeft={<Gear2Icon />}
          radius="rounded"
        />
        <Username>
          <UserAvatar size={34} />
          <LabelUsername>{username}</LabelUsername>
        </Username>
      </Heading>
      <Body data-trend={trend}>
        <BodyGroup>
          <PnlUsd>
            {pnlUsd === undefined ? (
              '—'
            ) : (
              <Numeric
                value={pnlUsd}
                preset="currency-amount-short"
                currency="$"
              />
            )}
          </PnlUsd>
          <PnlLabel>
            {t(USER_TRADING_ANALYTICS_PERIOD_LABEL_MAP[period]).toUpperCase()}{' '}
            {t('common.pnl')}
          </PnlLabel>
        </BodyGroup>
        <BodyGroup>
          <PnlPercents>
            {pnlPercents === undefined ? (
              '—'
            ) : (
              <>
                <Numeric value={pnlPercents} preset="percents" forceSign />%
              </>
            )}
          </PnlPercents>
          {chartData.length > 0 ? (
            <Chart data={chartData} changePercentage={pnlPercents ?? 0} />
          ) : undefined}
        </BodyGroup>
      </Body>
    </Root>
  )
}

const Root = styled.div`
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.2);
  padding: 3px;
  display: grid;

  &[data-variant='row'] {
    grid-template-columns: 1fr 1fr;
  }
  &[data-variant='square'] {
    grid-template-rows: auto 1fr;
  }
`

const Heading = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  padding-inline: 12px 8px;
  padding-block: 8px;

  > *:last-child {
    grid-column: 1 / -1;
  }
`

const LabelUsername = styled(Typography).attrs({
  variant: 'body1-semi-bold',
  component: 'span',
})`
  color: var(--text-12);
`

const Label = styled(Typography).attrs({
  variant: 'body3-medium',
  component: 'span',
})`
  color: var(--text-9);
`

const Body = styled.div`
  display: grid;
  gap: 4px;
  padding-block: 10px;
  padding-inline: 8px;

  border-radius: 8px;
  background: var(--backgroundA-3);

  color: var(--text-9);
  text-align: right;
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;

  --pnl-color: var(--text-12);

  &[data-trend='up'] {
    --pnl-color: var(--candleGreen-9);
  }
  &[data-trend='down'] {
    --pnl-color: var(--candleRed-9);
  }
`

const SettingsButton = styled(Button)`
  color: var(--text-9);
`

const Username = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  gap: 8px;
`

const PnlUsd = styled(Typography).attrs({
  variant: 'body1-semi-bold',
  component: 'span',
})`
  color: var(--pnl-color);
`

const PnlPercents = styled(Typography).attrs({
  variant: 'body4-medium',
  component: 'span',
})`
  background: var(--backgroundA-2);
  color: var(--pnl-color);
  border-radius: 55px;

  padding-inline: 10px;
  height: 20px;
  place-content: center;
`

const PnlLabel = styled(Typography).attrs({
  variant: 'body4-medium',
  component: 'span',
})`
  color: var(--text-9);
`

const Chart = styled(SummaryChart)`
  height: 20px;
  width: 80px;
`

const BodyGroup = styled.div`
  display: grid;
  grid-template-columns: auto auto;
  align-items: center;
  justify-content: space-between;
  justify-items: start;
  gap: 8px;
`
